import { useEffect, useState } from 'react';
import { Box, Chip, Divider, Pagination, Paper, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';
import CustomTooltip from './CustomTooltip';
import SerpModalButton from './SerpModalButton';
import { PlaItem } from '../types';

interface Props {
    items: PlaItem[];
    columns?: number;
    pageSize?: number;
    showBrandChip?: boolean;
    totalCount?: number;
}

export default function RightPanelAds({ items, columns = 2, pageSize = 10, showBrandChip = false, totalCount }: Props) {
    const [page, setPage] = useState(1);
    useEffect(() => { setPage(1); }, [items, columns]);
    const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
    const start = (page - 1) * pageSize;
    const current = items.slice(start, start + pageSize);
    const displayCount = totalCount !== undefined ? totalCount : items.length;
    return (
        <Stack spacing={1.5} >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                        sx={{
                            color: '#333C47',
                            fontFamily: '"Public Sans", sans-serif',
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '120%',
                        }}
                    >
                        Right panel Ads
                    </Typography>
                </Stack>
                <Typography variant="caption" fontSize={14} color="text.secondary">{displayCount} results found</Typography>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 1 }}>
                {current.map((ad, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, backgroundColor: 'background.paper', position: 'relative' }}>
                        <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                {showBrandChip && (((ad as any).brandInfringementTitle) || ((ad as any).brandInfringementDescription)) && (
                                    <Chip label="Brand" color="error" size="small" sx={{ height: 16 }} />
                                )}
                                <CustomTooltip title={`search term: ${ad.searchTerm}`} placement="top">
                                    <svg style={{ cursor: 'help' }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <g clip-path="url(#clip0_3494_16133)">
                                            <rect width="16" height="16" rx="8" fill="white" />
                                            <path d="M8.00016 10.6673V8.00065M8.00016 5.33398H8.00683M14.6668 8.00065C14.6668 11.6825 11.6821 14.6673 8.00016 14.6673C4.31826 14.6673 1.3335 11.6825 1.3335 8.00065C1.3335 4.31875 4.31826 1.33398 8.00016 1.33398C11.6821 1.33398 14.6668 4.31875 14.6668 8.00065Z" stroke="#5E6D7B" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round" />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_3494_16133">
                                                <rect width="16" height="16" rx="8" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </CustomTooltip>
                            </Stack>
                        </Box>
                        <Stack spacing={0.75}>
                            <Box sx={{ height: 60, width: 60, borderRadius: '0', bgcolor: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {ad.img ? <Box component="img" src={ad.img} alt={ad.title} sx={{ width: '100%', height: '100%', objectFit: 'contain', transformOrigin: 'center center' }} /> : <Iconify icon="mdi:image-outline" fontSize="small" />}
                            </Box>
                            <Box sx={{
                                position: 'relative',
                                ...(showBrandChip && ((ad as any).brandInfringementTitle || (ad as any).brandInfringementDescription) && {
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        width: '120px',
                                        height: '16px',
                                        background: 'linear-gradient(90deg, transparent, background.paper 40%)',
                                        pointerEvents: 'none'
                                    }
                                })
                            }}>
                                <Typography
                                    sx={{
                                        lineClamp: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                        color: '#637381',
                                        textOverflow: 'ellipsis',
                                        fontFamily: '"Public Sans", sans-serif',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: '120%',
                                    }}
                                >
                                    {ad.title}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    color: '#919EAB',
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    lineHeight: '120%',
                                }}
                            >
                                {ad.price}
                            </Typography>
                            <Stack direction="row" spacing={0.5} pt={'16px'} display="flex" flexDirection="column" flexWrap="wrap">
                                <Stack direction="row" justifyContent="space-between" spacing={0.5}>
                                    <Typography
                                        sx={{
                                            color: '#919EAB',
                                            fontFamily: '"Public Sans", sans-serif',
                                            fontSize: '13px',
                                            fontStyle: 'normal',
                                            fontWeight: 400,
                                            lineHeight: '140%',
                                        }}
                                    >
                                        Position
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: '#637381',
                                            fontFamily: '"Public Sans", sans-serif',
                                            fontSize: '14px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '140%',
                                        }}
                                    >
                                        {`#${ad.position}`}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={0.5}>
                                    <Typography
                                        sx={{
                                            color: '#919EAB',
                                            fontFamily: '"Public Sans", sans-serif',
                                            fontSize: '13px',
                                            fontStyle: 'normal',
                                            fontWeight: 400,
                                            lineHeight: '140%',
                                        }}
                                    >
                                        Frequency
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: '#637381',
                                            fontFamily: '"Public Sans", sans-serif',
                                            fontSize: '14px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '140%',
                                        }}
                                    >
                                        {`${ad.frequency}%`}
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Box
                                sx={{
                                    mt: 2,
                                    mx: -1.5,
                                    mb: -1.5,
                                    borderTop: '1px solid rgba(145, 158, 171, 0.20)',
                                    background: '#F8F9FA',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 1,
                                }}
                            >
                                <SerpModalButton
                                    gcsUrl={
                                        ad.device_type === 'MOBILE'
                                            ? (ad as any)._gcs_uri_mobile
                                            : (ad as any)._gcs_uri_desktop
                                    }
                                    deviceType={ad.device_type}
                                    iconType={ad.device_type === 'MOBILE' ? 'mobile' : 'desktop'}
                                    searchTerm={ad.searchTerm}
                                />
                            </Box>
                        </Stack>
                    </Paper>
                ))}
            </Box>
            {pageCount > 1 && (
                <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                    <Pagination color="standard" size="small" count={pageCount} page={page} onChange={(_, p) => setPage(p)} />
                </Stack>
            )}
        </Stack>
    );
}


